import React, { useState, useRef, useEffect } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import { debounce } from 'lodash'
import { Typography, Tooltip, makeStyles } from '@material-ui/core'
import PictoIcon from './PictoIcon'

const getColor = (theme, value) => {
  switch (value) {
    case 2:
      return theme.palette.common.green
    case 1:
      return theme.palette.common.yellow
    case 0:
      return theme.palette.common.red
    default:
      return theme.palette.common.white
  }
}

const useStyles = makeStyles((theme) => ({
  root: ({ disabled }) => ({
    opacity: disabled ? 0.2 : 1,
    '&:hover': {
      boxShadow: 'unset',
    },
  }),
  container: ({ sideLength }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: sideLength,
  }),
  topContainer: {
    border: '2px solid black',
    borderBottom: '1px solid black',
    backgroundColor: 'white',
  },
  bottomContainer: {
    border: '2px solid black',
    borderTop: '1px solid black',
    backgroundColor: ({ value }) => getColor(theme, value),
  },
  icon: ({ sideLength }) => ({
    fontSize: 0.55 * sideLength,
    color: theme.palette.common.black,
  }),
  value: ({ sideLength }) => ({
    fontSize: 0.5 * sideLength,
    fontWeight: 800,
    color: theme.palette.common.black,
  }),
  tooltip: { fontSize: '0.8rem' },
}))

const Domino = ({
  value,
  handicap,
  disabled,
  className = '',
  tabIndex = 0,
  tooltip = '',
  placement = 'top',
  ariaLabel = '',
}) => {
  const [sideLength, setSideLength] = useState(0)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const rootRef = useRef(null)
  const classes = useStyles({
    sideLength,
    value,
    disabled,
  })

  const handleKeyDown = (event) => {
    if (event.key === 'Escape' && tooltipOpen) {
      setTooltipOpen(false)
    }
  }

  const getLabel = () => {
    switch (value) {
      case 2:
        return 'A'
      case 1:
        return 'B'
      case 0:
        return 'C'
      default:
        return ''
    }
  }

  useEffect(() => {
    let resizeObserver = null
    if (rootRef) {
      resizeObserver = new ResizeObserver(
        debounce((entries) => {
          setSideLength(entries[0].contentRect.width)
        }, 200),
      )

      resizeObserver.observe(rootRef.current)
    }
    return () => resizeObserver.disconnect()
  }, [])

  return (
    <Tooltip
      placement={placement}
      title={tooltip}
      id={handicap}
      classes={{ tooltip: classes.tooltip }}
      open={tooltipOpen}
      onOpen={() => setTooltipOpen(true)}
      onClose={() => setTooltipOpen(false)}
    >
      <div
        ref={rootRef}
        aria-label={ariaLabel}
        className={`${classes.root} ${className}`}
        tabIndex={tabIndex}
        aria-hidden={tabIndex === -1}
        aria-describedby={handicap}
        role="listitem"
        onKeyDown={handleKeyDown}
        onFocus={() => setTooltipOpen(true)}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onBlur={() => setTooltipOpen(false)}
      >
        <div
          className={`${classes.container} ${classes.topContainer}`}
          aria-hidden="true"
        >
          <PictoIcon gutterBottom={false} className={classes.icon}>
            {handicap}
          </PictoIcon>
        </div>
        <div
          className={`${classes.container} ${classes.bottomContainer}`}
          aria-hidden="true"
        >
          <Typography gutterBottom={false} className={classes.value}>
            {getLabel()}
          </Typography>
        </div>
      </div>
    </Tooltip>
  )
}

export default Domino
